import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApproveInvoicesInput,
  AssignInvoiceInput,
  CompleteDocumentsInput,
  CreateInvoiceInput,
  DisputeInvoiceInput,
  Invoice,
  InvoiceDetail,
  InvoiceListQuery,
  InvoiceListResult,
  InvoiceListSummary,
  MarkInvoicePaidInput,
  ProjectSuggestion,
  RegisterPaymentInput,
  SetInvoiceEmployeesInput,
  SettleCorrectionInput,
  UnsettledCorrection,
  UpdateBookkeepingInput,
} from '@faktorplus/shared';
import { apiRequest } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export function useInvoiceList(
  query: Partial<InvoiceListQuery>,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.invoices.list(query),
    queryFn: () => apiRequest<InvoiceListResult>('/invoices', { query }),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

/** Hard ceiling from `paginationQuerySchema`. */
const MAX_PAGE_SIZE = 200;

export interface AllInvoicesResult {
  items: Invoice[];
  /** Totals for the whole filtered set — identical on every page, taken once. */
  summary: InvoiceListSummary;
}

/**
 * Pages through the whole filtered result set.
 *
 * Serves two screens that must never truncate: the CSV export (a spreadsheet
 * missing rows is worse than none) and payables, which the client reads as one
 * complete statement of what is owed (24.09.2026), not page by page.
 */
export async function fetchAllInvoices(
  query: Partial<InvoiceListQuery>,
): Promise<AllInvoicesResult> {
  const items: Invoice[] = [];
  let summary: InvoiceListSummary | undefined;

  for (let page = 1; ; page += 1) {
    const result = await apiRequest<InvoiceListResult>('/invoices', {
      query: { ...query, page, pageSize: MAX_PAGE_SIZE },
    });

    summary ??= result.summary;
    items.push(...result.items);
    if (items.length >= result.total || result.items.length === 0) break;
  }

  return { items, summary: summary! };
}

/** The complete filtered list as one query — what the payables screen reads. */
export function useAllInvoices(query: Partial<InvoiceListQuery>) {
  return useQuery({
    queryKey: queryKeys.invoices.list(query),
    queryFn: () => fetchAllInvoices(query),
    placeholderData: (previous) => previous,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.invoices.detail(id ?? ''),
    queryFn: () => apiRequest<InvoiceDetail>(`/invoices/${id}`),
    enabled: Boolean(id),
  });
}

/** Reads the vendor's recent history to pre-fill the project on the assign screen. */
export function useProjectSuggestion(vendorId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.invoices.suggestion(vendorId ?? ''),
    queryFn: () =>
      apiRequest<ProjectSuggestion | null>(`/invoices/suggestions/by-vendor/${vendorId}`),
    enabled: Boolean(vendorId),
    staleTime: 60 * 1000,
  });
}

/**
 * Every workflow action touches lists, counters and the dashboard, so they all
 * share one invalidation. Being slightly broad here is much cheaper than a
 * screen that silently shows a stale status.
 */
function useInvalidateInvoices() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.reports.dashboard });
    void queryClient.invalidateQueries({ queryKey: queryKeys.reports.activity });
  };
}

export function useCreateInvoice() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (input: CreateInvoiceInput) =>
      apiRequest<Invoice>('/invoices', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

/** Ledger position and payment method — editable at every status. */
export function useUpdateBookkeeping() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBookkeepingInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/bookkeeping`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

/** Replaces the whole set of people the invoice was bought for. */
export function useSetInvoiceEmployees() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SetInvoiceEmployeesInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/employees`, { method: 'PUT', body: input }),
    onSuccess: invalidate,
  });
}

/** Moves a mis-keyed invoice to "Usunięte". Restorable — see `useRestoreInvoice`. */
export function useDeleteInvoice() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Brings a deleted invoice back, in the state it was removed in. */
export function useRestoreInvoice() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<Invoice>(`/invoices/${id}/restore`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useAssignInvoice() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssignInvoiceInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/assign`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useApproveInvoice() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (id: string) => apiRequest<Invoice>(`/invoices/${id}/approve`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useDisputeInvoice() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DisputeInvoiceInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/dispute`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useApproveForPayment() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (input: ApproveInvoicesInput) =>
      apiRequest<{ approvedCount: number }>('/invoices/approve-for-payment', {
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

/** Open ± balances of correcting invoices — the payables section reads this. */
export function useUnsettledCorrections(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.invoices.unsettledCorrections,
    queryFn: () => apiRequest<UnsettledCorrection[]>('/invoices/corrections/unsettled'),
    enabled: options.enabled ?? true,
  });
}

/** Settles a correction: against an unpaid invoice, or by hand without one. */
export function useSettleCorrection() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SettleCorrectionInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/correction-settlement`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidate,
  });
}

/** Owner signs off, after the fact, on a payment released without them. */
export function useRecordOwnerApproval() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<Invoice>(`/invoices/${id}/owner-approval`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/** Owner sends an invoice back to the site manager for another look. */
export function useReturnToManager() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<Invoice>(`/invoices/${id}/return-to-manager`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useMarkPaid() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MarkInvoicePaidInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/mark-paid`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

/** One transfer against the invoice — "zapłata częściowa" of 24.09.2026. */
export function useRegisterPayment() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RegisterPaymentInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/payments`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

/** Removes a mis-keyed transfer, while the invoice is still open. */
export function useRemovePayment() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, paymentId }: { id: string; paymentId: string }) =>
      apiRequest<Invoice>(`/invoices/${id}/payments/${paymentId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useCompleteDocuments() {
  const invalidate = useInvalidateInvoices();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CompleteDocumentsInput }) =>
      apiRequest<Invoice>(`/invoices/${id}/complete-documents`, { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}
